import { Button, Card, Checkbox, Form, Input, Select, Space, Table, Tag, Tooltip } from 'antd';
import { DeleteOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLastLocationSave, getLocationById, getMerchandisingForLocation } from '../../offices/offices-selectors';
import { capitalize } from 'lodash';
import { createLocation } from '../../offices/offices-actions';
import { getWriteTabsPermissions } from '../../../store/user/user-reducer';
import { formItemMap } from '@med-fe/ui';
import { merchandisingConfig } from '../configs/merchandising-config';
import {
  clearDefaultOfficeBrandedFixtures,
  fetchBrandedFixtures,
  fetchDefaultOfficeBrandedFixtures,
  fetchOfficeFixtures,
  searchBrandedFixture,
} from '../../branded-fixtures/branded-fixtures-actions';
import {
  getAllBrandedFixtures,
  getBrandedFixturesForGrid,
  getDefaultOfficeBrandedFixtures,
  getDefaultOfficeBrandedFixturesLoading,
  getOfficeFixtures,
  getOfficeFixturesLoading,
} from '../../branded-fixtures/branded-fixtures-selectors';

function MerchandisingDetails({ locId }) {
  const [form] = Form.useForm();
  const [isDisabled, setIsDisabled] = useState(true);
  const [selectedFixtureIds, setSelectedFixtureIds] = useState<number[]>([]);
  const [selectedFixtureQuantities, setSelectedFixtureQuantities] = useState<Record<number, number>>({});
  const [fixtureSearch, setFixtureSearch] = useState('');
  const [vendor, setVendor] = useState<string>();
  const [collection, setCollection] = useState<string>();
  const [isGeneric, setIsGeneric] = useState<boolean>();
  const [overrideRecommendations, setOverrideRecommendations] = useState(false);
  const [fixturesDirty, setFixturesDirty] = useState(false);
  const [selectedFixtures, setSelectedFixtures] = useState<any[]>([]);
  const [officeFixtureQuantityAdjustments, setOfficeFixtureQuantityAdjustments] = useState<Record<number, number>>({});
  const savePending = useRef(false);
  const recommendationDefaultApplied = useRef(false);
  const officeFixturesLoadingSeen = useRef(false);
  const defaultFixturesLoadingSeen = useRef(false);
  const requestedLocationSid = useRef<number | null>(null);
  const requestedOfficeNumber = useRef<string | null>(null);
  const dispatch = useDispatch();
  const data = useSelector((state) => getMerchandisingForLocation(state, locId));
  const lastLocationSave = useSelector(getLastLocationSave);
  const location = useSelector((state) => getLocationById(state, locId));
  const isEditAllowed = useSelector(getWriteTabsPermissions).merchandising;
  const officeFixtures = useSelector(getOfficeFixtures);
  const officeFixturesLoading = useSelector(getOfficeFixturesLoading);
  const availableFixtures = useSelector(getBrandedFixturesForGrid) || [];
  const defaultOfficeBrandedFixtures = useSelector(getDefaultOfficeBrandedFixtures) || [];
  const defaultOfficeBrandedFixturesLoading = useSelector(getDefaultOfficeBrandedFixturesLoading);
  const allFixtures = useSelector(getAllBrandedFixtures) || [];

  const normalizeText = (value: string) => (value || '').trim().toLowerCase();
  const fixtureId = (fixture: any) => Number(fixture?.id ?? fixture?.brandedFixtureSid ?? fixture?.sid);
  const fixtureVendorName = (fixture: any) => fixture?.vendorName || fixture?.vendor || '';
  const fixtureCollectionName = (fixture: any) => fixture?.collection || fixture?.collectionName || '';
  const fixtureVendorCollectionKey = (fixture: any) => {
    const fixtureVendor = normalizeText(fixtureVendorName(fixture));
    const fixtureCollection = normalizeText(fixtureCollectionName(fixture));

    return fixtureVendor || fixtureCollection ? `${fixtureVendor}::${fixtureCollection}` : '';
  };
  const officeFixtureId = (fixture: any) =>
    Number(fixture?.brandedFixtureSid ?? fixture?.fixtureSid ?? fixture?.id ?? fixture?.sid);
  const visibleOfficeFixtures = officeFixtures
    .map((fixture: any) => ({
      ...fixture,
      quantity:
        Math.max(0, Number(fixture.quantity) || 1) + (officeFixtureQuantityAdjustments[officeFixtureId(fixture)] || 0),
    }))
    .filter((fixture: any) => fixture.quantity > 0);
  const existingFixtureIds = Array.from(new Set(visibleOfficeFixtures.map(officeFixtureId).filter((id) => id > 0)));
  const existingVendorCollectionKeys = new Set(
    visibleOfficeFixtures.map(fixtureVendorCollectionKey).filter((key: string) => key)
  );
  const recommendedVendorCollectionKeys = new Set(
    defaultOfficeBrandedFixtures.map(fixtureVendorCollectionKey).filter((key: string) => key)
  );
  const hasAssignedRecommendedFixture = Array.from(existingVendorCollectionKeys).some((key) =>
    recommendedVendorCollectionKeys.has(key)
  );
  const hasFixtureFilters = Boolean(fixtureSearch.trim() || vendor || collection || isGeneric !== undefined);
  const normalizedFixtureSearch = fixtureSearch.trim().toLowerCase();
  const filteredAvailableFixtures = availableFixtures.filter((fixture: any) => {
    const fixtureName = String(fixture.fixtureName || '').toLowerCase();
    const fixtureVendor = fixture.vendorName || fixture.vendor;
    const fixtureCollection = fixture.collection || fixture.collectionName;

    return (
      (!normalizedFixtureSearch || fixtureName.includes(normalizedFixtureSearch)) &&
      (!vendor || fixtureVendor === vendor) &&
      (!collection || fixtureCollection === collection) &&
      (isGeneric === undefined || fixture.isGeneric === isGeneric)
    );
  });
  const vendorOptions = Array.from(
    new Set(allFixtures.map((fixture: any) => fixture.vendorName).filter(Boolean))
  ) as string[];
  const collectionOptions = Array.from(
    new Set(allFixtures.map((fixture: any) => fixture.collection).filter(Boolean))
  ) as string[];
  const defaultAvailableFixtures = defaultOfficeBrandedFixtures.filter((fixture: any) => {
    const key = fixtureVendorCollectionKey(fixture);

    return !key || !existingVendorCollectionKeys.has(key);
  });
  const defaultFixtureIds = defaultAvailableFixtures.map(fixtureId);
  const searchFixtures = filteredAvailableFixtures.filter(
    (fixture: any) => !defaultFixtureIds.includes(fixtureId(fixture))
  );
  const fixtureColumns = (showGenericTag = true) => [
    {
      title: 'Fixture',
      dataIndex: 'fixtureName',
      key: 'fixtureName',
      width: 220,
      render: (value: string, fixture: any) => (
        <span>
          {value || `Fixture ${fixtureId(fixture)}`}
          {showGenericTag && fixture.isGeneric && <Tag style={{ marginLeft: 8 }}>Generic</Tag>}
        </span>
      ),
    },
    {
      title: 'Product Vendor',
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 160,
      render: (_: string, fixture: any) => fixture.vendorName || fixture.vendor || '-',
    },
    {
      title: 'Collection',
      dataIndex: 'collection',
      key: 'collection',
      width: 160,
      render: (_: string, fixture: any) => fixture.collection || fixture.collectionName || '-',
    },
  ];
  const addFixtureActionColumn = {
    title: 'Action',
    key: 'action',
    width: 140,
    render: (_: any, fixture: any) => {
      const id = fixtureId(fixture);
      const alreadyAssociated = existingFixtureIds.includes(id);
      const quantity = selectedFixtureQuantities[id] || 0;

      if (quantity > 0) {
        return (
          <Space size={4}>
            <Button
              size='small'
              icon={<MinusOutlined />}
              aria-label={`Remove one ${fixture.fixtureName || 'fixture'}`}
              onClick={() => updateFixtureQuantity(id, quantity - 1)}
              disabled={!isEditAllowed}
            />
            <span>{quantity}</span>
            <Button
              size='small'
              icon={<PlusOutlined />}
              aria-label={`Add one ${fixture.fixtureName || 'fixture'}`}
              onClick={() => updateFixtureQuantity(id, quantity + 1)}
              disabled={!isEditAllowed}
            />
          </Space>
        );
      }

      if (alreadyAssociated) {
        return <Tag color='blue'>Associated</Tag>;
      }

      return (
        <Button size='small' icon={<PlusOutlined />} onClick={() => selectFixture(fixture)} disabled={!isEditAllowed}>
          Add
        </Button>
      );
    },
  };
  useEffect(() => {
    form.setFieldsValue({
      ...data,
    });
  }, [locId]);

  useEffect(() => {
    const locationSid = Number(locId);
    if (!locationSid || requestedLocationSid.current === locationSid) return;
    requestedLocationSid.current = locationSid;

    setSelectedFixtureIds([]);
    setSelectedFixtureQuantities({});
    setSelectedFixtures([]);
    setOfficeFixtureQuantityAdjustments({});
    recommendationDefaultApplied.current = false;
    officeFixturesLoadingSeen.current = false;
    defaultFixturesLoadingSeen.current = false;
    setOverrideRecommendations(false);
    setFixturesDirty(false);
    dispatch(fetchOfficeFixtures(locationSid));
    dispatch(fetchBrandedFixtures());
    dispatch(clearDefaultOfficeBrandedFixtures());
  }, [locId]);

  useEffect(() => {
    if (!location?.locationNumber || requestedOfficeNumber.current === location.locationNumber) return;
    requestedOfficeNumber.current = location.locationNumber;

    dispatch(fetchDefaultOfficeBrandedFixtures(location.locationNumber));
  }, [location?.locationNumber]);

  useEffect(() => {
    if (officeFixturesLoading) {
      officeFixturesLoadingSeen.current = true;
    }

    if (defaultOfficeBrandedFixturesLoading) {
      defaultFixturesLoadingSeen.current = true;
    }

    if (
      !recommendationDefaultApplied.current &&
      officeFixturesLoadingSeen.current &&
      defaultFixturesLoadingSeen.current &&
      !officeFixturesLoading &&
      !defaultOfficeBrandedFixturesLoading
    ) {
      setOverrideRecommendations(hasAssignedRecommendedFixture);
      recommendationDefaultApplied.current = true;
    }

    if (officeFixturesLoading) {
      return;
    }

    const persistedFixtureIds = new Set(officeFixtures.map(officeFixtureId));
    setSelectedFixtureIds((current) => current.filter((id) => !persistedFixtureIds.has(id)));
    setSelectedFixtures((current) => current.filter((fixture) => !persistedFixtureIds.has(fixtureId(fixture))));
    setOfficeFixtureQuantityAdjustments((current) =>
      Object.fromEntries(Object.entries(current).filter(([id]) => persistedFixtureIds.has(Number(id))))
    );
  }, [officeFixtures, officeFixturesLoading, defaultOfficeBrandedFixturesLoading, hasAssignedRecommendedFixture]);

  useEffect(() => {
    if (!savePending.current || Number(lastLocationSave?.id) !== Number(locId)) return;

    savePending.current = false;
    setFixtureSearch('');
    setVendor(undefined);
    setCollection(undefined);
    setIsGeneric(undefined);
    setSelectedFixtureIds([]);
    setSelectedFixtureQuantities({});
    setSelectedFixtures([]);
    setOfficeFixtureQuantityAdjustments({});
    setFixturesDirty(false);
  }, [lastLocationSave, locId]);

  useEffect(() => {
    if (!overrideRecommendations) return;
    const timer = window.setTimeout(() => {
      dispatch(
        searchBrandedFixture({
          fixtureName: fixtureSearch || null,
          vendorName: vendor || null,
          collection: collection || null,
          isGeneric: isGeneric ?? null,
          page: 0,
          size: 100,
        })
      );
    }, 250);

    return () => window.clearTimeout(timer);
  }, [fixtureSearch, vendor, collection, isGeneric, overrideRecommendations]);

  const selectFixture = (fixture: any) => {
    const id = fixtureId(fixture);
    if (!id || existingFixtureIds.includes(id) || selectedFixtureIds.includes(id)) return;
    setSelectedFixtureIds((current) => [...current, id]);
    setSelectedFixtureQuantities((current) => ({ ...current, [id]: 1 }));
    setSelectedFixtures((current) => [...current, fixture]);
    setFixturesDirty(true);
    setIsDisabled(false);
  };

  const updateFixtureQuantity = (id: number, quantity: number) => {
    if (quantity <= 0) {
      removeSelectedFixture(id);
      return;
    }

    setSelectedFixtureQuantities((current) => ({ ...current, [id]: quantity }));
    setFixturesDirty(true);
    setIsDisabled(false);
  };

  const removeSelectedFixture = (id: number) => {
    setSelectedFixtureIds((current) => current.filter((fixtureIdValue) => fixtureIdValue !== id));
    setSelectedFixtureQuantities((current) => {
      const next = { ...current };
      delete next[id];
      return next;
    });
    setSelectedFixtures((current) => current.filter((fixture) => fixtureId(fixture) !== id));
    setFixturesDirty(true);
    setIsDisabled(false);
  };

  const adjustOfficeFixtureQuantity = (id: number, change: number) => {
    setOfficeFixtureQuantityAdjustments((current) => ({ ...current, [id]: (current[id] || 0) + change }));
    setFixturesDirty(true);
    setIsDisabled(false);
  };

  const deleteOfficeFixture = (id: number) => {
    const fixture = officeFixtures.find((item: any) => officeFixtureId(item) === id);
    const quantity = Math.max(0, Number(fixture?.quantity) || 1);
    setOfficeFixtureQuantityAdjustments((current) => ({ ...current, [id]: -quantity }));
    setFixturesDirty(true);
    setIsDisabled(false);
  };

  const onSubmit = (formData: any) => {
    const brandedFixtures = [
      ...visibleOfficeFixtures.map((fixture: any) => ({
        brandedFixtureSid: officeFixtureId(fixture),
        quantity: fixture.quantity,
      })),
      ...selectedFixtureIds.map((id) => ({
        brandedFixtureSid: id,
        quantity: selectedFixtureQuantities[id] || 1,
      })),
    ];
    const brandedFixtureIds = brandedFixtures.map((fixture) => fixture.brandedFixtureSid);
    const payload = {
      fixture: {
        ...formData,
        id: data.merchandisingId,
        ...(fixturesDirty && { brandedFixtureIds, brandedFixtures }),
      },
      id: locId,
    };
    savePending.current = true;
    dispatch(createLocation(payload));
  };

  return (
    <Form
      form={form}
      layout='vertical'
      className='vendor-details-form merchandising-details-form'
      style={{ width: '100%', maxWidth: '100%', flex: '1 1 100%' }}
      onFinish={(formData) => {
        onSubmit(formData);
        setIsDisabled(true);
      }}
      onValuesChange={() => setIsDisabled(false)}
    >
      {Object.entries(merchandisingConfig).map(([key, value]: any) => {
        const params = {
          key,
          label: capitalize(value.label),
          name: key,
          rules: value.rules,
          data: value.data,
          style: { marginBottom: 16, ...(key === 'merchandisingTier' && { width: 170 }) },
        };
        return <Form.Item {...params}>{formItemMap(value, !isEditAllowed)}</Form.Item>;
      })}

      <div style={{ width: '100%', maxWidth: '100%', flex: '1 1 100%' }}>
        {visibleOfficeFixtures.length > 0 && (
          <Card
            bodyStyle={{ padding: '14px' }}
            title='Branded Fixtures'
            className='merchandising-section-card'
            style={{ width: '100%', maxWidth: '100%', marginBottom: 16 }}
          >
            <Table
              columns={[
                ...fixtureColumns(false),
                {
                  title: 'Quantity',
                  dataIndex: 'quantity',
                  key: 'quantity',
                  width: 90,
                },
                {
                  title: 'Action',
                  dataIndex: 'operation',
                  key: 'operation',
                  fixed: 'right',
                  width: 110,
                  render: (_: any, fixture: any) => (
                    <Space size={4}>
                      <Tooltip placement='top' title='Remove one'>
                        <Button
                          size='small'
                          type='primary'
                          icon={<MinusOutlined />}
                          onClick={() => adjustOfficeFixtureQuantity(officeFixtureId(fixture), -1)}
                          aria-label={`Remove one ${fixture.fixtureName || 'fixture'}`}
                          disabled={!isEditAllowed || fixture.quantity <= 1}
                        />
                      </Tooltip>
                      <Tooltip placement='top' title='Delete fixture'>
                        <Button
                          size='small'
                          type='primary'
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => deleteOfficeFixture(officeFixtureId(fixture))}
                          aria-label={`Delete ${fixture.fixtureName || 'fixture'}`}
                          disabled={!isEditAllowed}
                        />
                      </Tooltip>
                      <Tooltip placement='top' title='Add one'>
                        <Button
                          size='small'
                          icon={<PlusOutlined />}
                          onClick={() => adjustOfficeFixtureQuantity(officeFixtureId(fixture), 1)}
                          aria-label={`Add one ${fixture.fixtureName || 'fixture'}`}
                          disabled={!isEditAllowed}
                        />
                      </Tooltip>
                    </Space>
                  ),
                },
              ]}
              bordered
              pagination={false}
              dataSource={visibleOfficeFixtures}
              rowKey={(fixture: any) => String(fixture.id ?? officeFixtureId(fixture))}
              locale={{ emptyText: 'No fixtures associated' }}
              scroll={{ y: 'calc(35vh - 10em)' }}
            />
          </Card>
        )}

        <Card
          bodyStyle={{ padding: '14px' }}
          title='Add Branded Fixtures'
          className='merchandising-section-card'
          style={{ width: '100%', maxWidth: '100%' }}
        >
          <Checkbox
            checked={overrideRecommendations}
            disabled={!isEditAllowed}
            onChange={(event) => setOverrideRecommendations(event.target.checked)}
            style={{ marginBottom: 14 }}
          >
            Override Recommendations
          </Checkbox>

          {overrideRecommendations && (
            <Space wrap align='end' style={{ marginBottom: 14 }}>
              <Form.Item label='Fixture Name' style={{ marginBottom: 0 }}>
                <Input
                  style={{ width: 220, backgroundColor: 'transparent' }}
                  placeholder='Search by fixture name'
                  value={fixtureSearch}
                  onChange={(event) => setFixtureSearch(event.target.value)}
                  allowClear
                />
              </Form.Item>
              <Form.Item label='Product Vendor' style={{ marginBottom: 0 }}>
                <Select
                  style={{ width: 160 }}
                  placeholder='Select Product Vendor'
                  value={vendor}
                  onChange={setVendor}
                  allowClear
                  options={vendorOptions.map((value) => ({ label: value, value }))}
                />
              </Form.Item>
              <Form.Item label='Collection' style={{ marginBottom: 0 }}>
                <Select
                  style={{ width: 160 }}
                  placeholder='Select collection'
                  value={collection}
                  onChange={setCollection}
                  allowClear
                  options={collectionOptions.map((value) => ({ label: value, value }))}
                />
              </Form.Item>
              <Form.Item label='Generic' style={{ marginBottom: 0 }}>
                <Select
                  style={{ width: 140 }}
                  placeholder='Select'
                  value={isGeneric}
                  onChange={setIsGeneric}
                  allowClear
                  options={[
                    { label: 'Yes', value: true },
                    { label: 'No', value: false },
                  ]}
                />
              </Form.Item>
            </Space>
          )}

          {!overrideRecommendations && (
            <Table
              title={() => 'Recommended Fixtures'}
              className='merchandising-fixture-table'
              columns={[...fixtureColumns(false), addFixtureActionColumn]}
              dataSource={defaultAvailableFixtures}
              bordered
              pagination={false}
              rowKey={(fixture: any) => String(fixtureId(fixture))}
              locale={{ emptyText: 'No recommended fixtures found for this office vendor collection' }}
            />
          )}

          {overrideRecommendations && hasFixtureFilters && (
            <Table
              title={() => 'Available fixtures'}
              className='merchandising-fixture-table'
              columns={[...fixtureColumns(false), addFixtureActionColumn]}
              dataSource={searchFixtures}
              bordered
              pagination={false}
              rowKey={(fixture: any) => String(fixtureId(fixture))}
              locale={{ emptyText: 'No fixtures found' }}
            />
          )}

          {selectedFixtures.length > 0 && (
            <Table
              title={() => 'SELECTED FIXTURES'}
              className='merchandising-fixture-table'
              columns={[
                ...fixtureColumns(false),
                {
                  title: 'Quantity',
                  key: 'quantity',
                  width: 90,
                  render: (_: any, fixture: any) => selectedFixtureQuantities[fixtureId(fixture)] || 1,
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 80,
                  render: (_: any, fixture: any) => (
                    <Button
                      type='link'
                      onClick={() => removeSelectedFixture(fixtureId(fixture))}
                      disabled={!isEditAllowed}
                    >
                      Remove
                    </Button>
                  ),
                },
              ]}
              dataSource={selectedFixtures}
              bordered
              pagination={false}
              rowKey={(fixture: any) => String(fixtureId(fixture))}
              style={{ marginTop: 14, marginBottom: 14 }}
            />
          )}
        </Card>
      </div>

      <Form.Item>
        <Button type='primary' htmlType='submit' className={'btn-form-action'} disabled={!isEditAllowed || isDisabled}>
          SAVE
        </Button>
      </Form.Item>
    </Form>
  );
}

export default MerchandisingDetails;
